import { type ComponentType, useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter } from "solito/router";
import { match } from "ts-pattern";
import { FullscreenSpinner, isWeb } from "@medbillai/ui";
import { loginUrl } from "@medbillai/utils/urls";
import { analyticsService } from "../../lib/analytics";
import { useAuth } from "../../lib/auth";
import type { FlowState, FlowTransition } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { useSignUpOnboardingStore } from "../../lib/sign-up-onboarding/signUpOnboardingStore";
import { deriveInitial } from "./deriveInitial";
import { trackScreenEnteredEvent, trackTransitionEvent } from "./flowTracking";
import { type AuthHandoff, ONBOARDING_VERSION, type OnboardingConfig, OnboardingFlowV3, type OnboardingTransitionProps, Screen } from "./OnboardingFlowV3";
import { useOnboardingState } from "./utils/useOnboardingState";
export type OnboardingPhase = "preauth" | "postauth";
export type OnboardingShellV3Props = {
  phase: OnboardingPhase;
  /** Per-platform screen-transition wrapper — see `OnboardingFlowV3`. */
  Transition: ComponentType<OnboardingTransitionProps>;
};

/**
 * General-user entry of the shared onboarding flow (`skipFaFlow: true` — no
 * partner content, no FA pre-screener). Preauth renders the value-prop beats
 * and hands off to the standard login (no invite token); postauth resumes
 * from the same canonical presence flags the partner (FFGA) entry uses —
 * general users have no bound partner, so no co-branding is derived and
 * events report the plain `v3.0` tag.
 */
export function OnboardingShellV3({
  phase,
  Transition
}: OnboardingShellV3Props) {
  const {
    push,
    replace
  } = useRouter();
  const {
    setIsSignUpOnboarding
  } = useSignUpOnboardingStore();
  const {
    refreshProfile
  } = useAuth();
  const resume = useOnboardingState();
  useEffect(() => {
    if (phase !== "preauth") {
      return;
    }
    analyticsService.trackEvent("SignUpOnboardingFlowStarted", {
      onboardingVersion: ONBOARDING_VERSION
    });
    // Web has no preauth beats — the marketing site and the signup hero own
    // the value props (v3.0 design: ValueProps is the native-only welcome),
    // so web hands straight to the signup screen. Mirrors v2's
    // `isWeb ? Intent : ValueProps` preauth split. The signup variant rides
    // the URL (like v2's intent variants) so the framing survives a refresh.
    if (isWeb) {
      setIsSignUpOnboarding(true);
      replace(loginUrl({
        variant: "signup"
      }));
    }
  }, [phase, replace, setIsSignUpOnboarding]);
  const handleAuthHandoff = useCallback((event: AuthHandoff) => {
    match(event).with({
      tag: "exitToSignup"
    }, () => {
      setIsSignUpOnboarding(true);
      push(loginUrl({
        variant: "signup"
      }));
    }).with({
      tag: "exitToLogin"
    }, () => {
      push(loginUrl());
    }).exhaustive();
  }, [push, setIsSignUpOnboarding]);
  const handleComplete = useCallback(async () => {
    setIsSignUpOnboarding(false);
    try {
      // Onboarding wrote segmentation (intent) post-auth; refresh the cached
      // profile so the home screen reflects it (e.g. bill-upload targeting)
      // instead of the stale login-time snapshot.
      await refreshProfile();
    } catch (error) {
      // Home refetches on its own — never strand the user on the spinner.
      sentryService.captureException(error);
    }
    replace("/");
  }, [replace, setIsSignUpOnboarding, refreshProfile]);
  const onTransition = useCallback((event: FlowTransition<OnboardingConfig>) => trackTransitionEvent(event, ONBOARDING_VERSION), []);
  const initial = useMemo(() => {
    if (phase === "preauth") {
      // Preauth never waits on the resume read — the visitor has no account.
      // Web holds the spinner while the effect above redirects to signup.
      return isWeb ? null : Screen.ValueProps();
    }
    if (resume.loading) {
      return null;
    }
    return deriveInitial({
      state: resume.state,
      skipFaFlow: true,
      topOfFunnel: Screen.Profile()
    }).initial;
  }, [phase, resume.loading, resume.state]);

  // Covers the initial screen too. Auth-handoff outcomes are self-transitions
  // (fresh same-tag state committed while the shell redirects), so same-tag
  // commits are skipped to keep screen events 1:1 with genuine entries.
  const lastTrackedTagRef = useRef<FlowState<OnboardingConfig>["tag"] | null>(null);
  const trackScreenEntered = useCallback((state: FlowState<OnboardingConfig>) => {
    if (state.tag === lastTrackedTagRef.current) {
      return;
    }
    lastTrackedTagRef.current = state.tag;
    trackScreenEnteredEvent(state, ONBOARDING_VERSION);
  }, []);
  if (initial === null) {
    return <FullscreenSpinner />;
  }
  return <OnboardingFlowV3 initial={initial} ctx={{
    isWeb,
    skipFaFlow: true
  }} onAuthHandoff={handleAuthHandoff} onComplete={handleComplete} onTransition={onTransition} onStateLoaded={trackScreenEntered} Transition={Transition} data-sentry-element="OnboardingFlowV3" data-sentry-component="OnboardingShellV3" data-sentry-source-file="OnboardingShellV3.tsx" />;
}